import { Injector, inject, makeEnvironmentProviders } from '@angular/core';
import { Router } from '@angular/router';

import { MemberProfilesService } from '@lib/members/data-access';
import { Prescription, PrescriptionsSearch, PrescriptionsService } from '@lib/prescriptions/data-access';
import { Store } from '@ngrx/store';
import { firstValueFrom, map } from 'rxjs';

import { provideChatState } from '@lib/chat/core/providers/chat-state.provider';
import { ChatCommunicationService } from '@lib/chat/core/services/chat-communication.service';
import { ChatNavigationService } from '@lib/chat/core/services/chat-navigation.service';
import { CHAT_NAVIGATION_HANDLERS, type ChatNavigationHandlers } from '@lib/chat/core/tokens/chat-navigation.tokens';
import { CHAT_API_URL, CHAT_LIB_CONFIG } from '@lib/chat/core/tokens/chat.tokens';
import { ChatLibConfig, ThreadData } from '@lib/chat/models/chat-config.interface';
import { ThreadType } from '@lib/chat/models/chat.interface';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';

import { CreateAppointmentModalType } from '@lib/core/models/create-appointment.interface';
import { MemberProfile } from '@lib/core/models/member.interface';
import { selectUser } from '@lib/core/stores/user/user.selectors';

import { environment } from '../../environments/environment';

export function provideChatLibConfig() {
  return makeEnvironmentProviders([
    { provide: CHAT_API_URL, useValue: environment.protectedApiUrl },
    {
      provide: CHAT_LIB_CONFIG,
      useFactory: (): ChatLibConfig => {
        const injector = inject(Injector);

        return {
          getUserInfo: () => {
            const store = injector.get(Store);
            const user = store.selectSignal(selectUser)();

            return {
              userId: user?.userId || '00000000-0000-0000-0000-000000000000',
              senderName: user?.fullName || 'Менеджер здоров`я',
            };
          },
          excludeThreadTypes: [ThreadType.AiAssistant],
          features: {
            showThreadTabs: true,
            showMedicalHistory: true,
            showPrescriptionsTab: true,
            showMemberProfile: true,
            showHealthCheckScheduler: true,
            showMemberComments: true,
            enableProfileNavigation: true,
            enableAppointmentCreation: true,
          },
          notificationConfig: {
            title: 'У вас нове повідомлення',
            icon: 'dist/assets/images/logo.svg',
            soundEnabled: true,
          },
          resolveThreadData: async (threadId: string) => {
            try {
              // Use captured injector
              const memberProfilesService = injector.get(MemberProfilesService);
              const chatApiService = injector.get(ChatCommunicationService);
              const prescriptionsService = injector.get(PrescriptionsService);

              const thread = await firstValueFrom(chatApiService.getThread(threadId));
              const prescriptionSearchPayload: PrescriptionsSearch = {
                page: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                sortOrder: 'Descending',
                userId: thread.ownerId,
              };
              let memberProfileInfo: MemberProfile | null = null;
              let memberPrescriptions: Prescription[] = [];

              try {
                memberProfileInfo = await firstValueFrom(memberProfilesService.getClientProfileByUserId(thread.ownerId));

                if (memberProfileInfo) {
                  memberPrescriptions = await firstValueFrom(prescriptionsService.POCPrescriptionsSearch(prescriptionSearchPayload).pipe(map(res => res.value)));
                }
              } catch (memberError) {
                console.warn('Could not load member data, continuing with thread only:', memberError);
              }

              return {
                thread,
                profile: memberProfileInfo,
                relatedItems: memberPrescriptions,
              } as ThreadData;
            } catch (error) {
              console.error('Error resolving thread data:', error);
              throw error;
            }
          },
        };
      },
      deps: [],
    },
    {
      provide: CHAT_NAVIGATION_HANDLERS,
      useFactory: (router: Router, memberProfilesService: MemberProfilesService, chatNavigationService: ChatNavigationService): ChatNavigationHandlers => {
        /** The guarantees tab addresses the member by profile id; chat knows them by user id. */
        const openGuarantees = (userId: string, queryParams: Record<string, string>, what: string) => {
          memberProfilesService.getClientProfileByUserId(userId).subscribe({
            next: memberProfile => router.navigate([`/members/${memberProfile.id}/guarantees`], { queryParams }),
            error: error => console.error(`Error navigating to ${what}:`, error),
          });
        };

        return {
          navigateToPrescription: (prescriptionId: string, userId: string) => {
            openGuarantees(userId, { prescription: prescriptionId }, 'prescription');
          },

          switchToPrescriptionTab: (prescriptionId: string) => {
            chatNavigationService.switchToPrescriptionTab(prescriptionId);
          },

          /**
           * Booking from chat, taken where the care plan is: the tab opens on the line and raises the
           * arrangement over it. The line is what says whether the booking is an appointment, a lab
           * order or a pharmacy one, so the message's own type is not consulted — and the arrangement
           * offers the rest of the course with it, which a modal opened on one prescription could not.
           */
          openCreateAppointmentModal: (userId: string, _modalType: CreateAppointmentModalType, metadata?) => {
            // TODO: remove both fields handling after mobile will use prescriptionIds only
            const [prescriptionId] = metadata?.prescriptionIds || (metadata?.prescriptionId ? [metadata.prescriptionId] : []);
            if (!prescriptionId) {
              console.error('Cannot arrange a booking from chat: the message names no prescription');

              return;
            }

            openGuarantees(userId, { prescription: prescriptionId, action: 'book' }, 'booking');
          },

          navigateToMemberProfile: (userId: string) => {
            memberProfilesService.getClientProfileByUserId(userId).subscribe({
              next: memberProfile => router.navigate([`/members/${memberProfile.id}/profile`]),
              error: error => console.error('Error navigating to member profile:', error),
            });
          },
        };
      },
      deps: [Router, MemberProfilesService, ChatNavigationService],
    },
    provideChatState(),
  ]);
}
